import React, { useState, useEffect, useCallback, useRef } from "react";
import { authService } from "../services/auth";
import { profileService } from "../services/profile";
import { supabase } from "../services/supabaseClient";
import toast from "react-hot-toast";
import AvatarCropModal from "../components/AvatarCropModal"; // Adicionar

const Profile = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [fullName, setFullName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [isPasswordChangeVisible, setIsPasswordChangeVisible] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null); // Novo estado para a imagem a ser recortada
  const [showCropModal, setShowCropModal] = useState(false); // Estado para controlar o modal

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);

      if (currentUser) {
        let userProfile = await profileService.getProfile(currentUser.id);
        if (!userProfile) {
          // Se o perfil não existir, cria um novo
          userProfile = await profileService.createProfile(currentUser.id, {
            full_name: currentUser.user_metadata?.full_name || "",
          });
        }
        setProfile(userProfile);
        setFullName(userProfile.full_name || "");
      }
    } catch (error) {
      toast.error("Erro ao carregar dados do perfil: " + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (user) {
        // Atualiza o full_name na tabela profiles
        await profileService.updateProfile(user.id, { full_name: fullName });

        // Opcional: Atualiza o user_metadata no Supabase Auth (se ainda for usado em algum lugar)
        await supabase.auth.updateUser({
          data: { full_name: fullName },
        });

        toast.success("Perfil atualizado com sucesso!");
        fetchUserData(); // Recarrega os dados para garantir consistência
      }
    } catch (error) {
      toast.error("Erro ao atualizar perfil: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (newPassword !== confirmPassword) {
        toast.error("As palavra-passes não coincidem.");
        return;
      }
      if (newPassword.length < 6) {
        toast.error("A palavra-passe deve ter no mínimo 6 caracteres.");
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success("Senha alterada com sucesso!");
      setNewPassword("");
      setConfirmPassword("");
      setIsPasswordChangeVisible(false); // Esconde o formulário após a alteração
    } catch (error) {
      toast.error("Erro ao alterar palavra-passe: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageToCrop(reader.result); // Define a imagem para o modal
        setShowCropModal(true); // Abre o modal
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleSaveCroppedImage = async (croppedBlob) => {
    setLoading(true);
    try {
      if (user && croppedBlob) {
        const croppedFile = new File([croppedBlob], `${user.id}.jpeg`, {
          type: "image/jpeg",
        });

        const avatarUrl = await profileService.uploadAvatar(
          user.id,
          croppedFile
        );
        setProfile((prevProfile) => ({
          ...prevProfile,
          avatar_url: avatarUrl,
        }));
        toast.success("Imagem de perfil atualizada com sucesso!");
      }
    } catch (error) {
      toast.error("Erro ao fazer upload da imagem: " + error.message);
    } finally {
      setLoading(false);
      setImageToCrop(null); // Limpa a imagem após o upload
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>A carregar perfil...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Nenhum usuário logado.</p>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <h1 className="text-3xl font-bold mb-6 text-center">Meu Perfil</h1>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4">Informações Básicas</h2>
        <div className="flex items-center space-x-4 mb-4">
          <img
            src={profile?.avatar_url || "https://via.placeholder.com/150"}
            alt="Avatar"
            className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
          />
          <div>
            <p className="text-lg font-medium">Email: {user.email}</p>
            <p className="text-lg font-medium">
              Função: {user.app_metadata?.role || user.user_metadata?.role || "Não definida"}
            </p>
          </div>
        </div>

        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div>
            <label
              htmlFor="fullName"
              className="block text-sm font-medium text-gray-700"
            >
              Nome Completo
            </label>
            <input
              type="text"
              id="fullName"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            {loading ? "Atualizando..." : "Atualizar Perfil"}
          </button>
        </form>

        <div className="mt-6">
          <label
            htmlFor="avatarUpload"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Alterar Imagem de Perfil
          </label>
          <input
            type="file"
            id="avatarUpload"
            accept="image/*"
            onChange={handleAvatarChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {showCropModal && (
          <AvatarCropModal
            src={imageToCrop}
            onClose={() => setShowCropModal(false)}
            onSave={handleSaveCroppedImage}
          />
        )}
      </div>

      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">Alterar Senha</h2>
        <button
          onClick={() => setIsPasswordChangeVisible(!isPasswordChangeVisible)}
          className="mb-4 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
        >
          {isPasswordChangeVisible
            ? "Esconder Formulário de Senha"
            : "Mostrar Formulário de Senha"}
        </button>

        {isPasswordChangeVisible && (
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Nova Senha
              </label>
              <input
                type="password"
                id="newPassword"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirmar Nova Senha
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              disabled={loading}
            >
              {loading ? "Alterando..." : "Alterar Senha"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Profile;
